/**
 * Gerakan tangan di atas sebuah lembar kedalaman — roda, mistar, kotak.
 *
 * `depthscale.ts` menjawab berapa piksel satu meter batuan. Berkas ini
 * menjawab pertanyaan berikutnya: apa yang terjadi ketika seseorang memutar
 * roda di atas lembar itu, menyeret mistarnya, atau menyapu sebuah interval —
 * dan ke mana pandangan harus pindah sesudahnya.
 *
 * Ia tinggal di `lib/` bersama aritmetika yang dipakainya, meski berupa hook.
 * Yang menentukan tempatnya bukan bentuknya melainkan apa yang diketahuinya:
 * skala kedalaman, dan tidak satu pun tentang layar yang memanggilnya.
 *
 * Tiga layar memandang lembar kedalaman — Log, korelasi, dan analisis — dan
 * tangan yang sama dipakai pada ketiganya. Gerakan yang berbeda di layar yang
 * berbeda bukan tiga perilaku, melainkan satu perilaku yang dua di antaranya
 * salah.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useElementSize, useStored } from "../hooks";
import {
  dragScale,
  intervalScale,
  resolveScale,
  stepScaleByGrid,
  wheelPixels,
  WHEEL_ZOOM_GAIN,
  type ScaleMode,
} from "./depthscale";

/** Seret sejauh ini masih sebuah klik, bukan pilihan interval. */
const BOX_MIN = 6;

/** Kotak zoom yang sedang diseret, dalam piksel isi lembar. */
export interface DepthBox {
  from: number;
  to: number;
}

export interface DepthSheet {
  /** Dipasang pada panel yang bergulir; ia mengukur sekaligus memasang roda. */
  attachPane: (element: HTMLDivElement | null) => void;
  paneHeight: number;
  /**
   * Geseran mendatar panel, nol bila `panX` mati.
   *
   * Dibaca oleh kepala track yang menempel di atas lembar: ia harus bergeser
   * bersama lembarnya, dan ia tidak berada di dalam panel yang bergulir.
   */
  scrollX: number;
  /** Skala yang berlaku dan tinggi lembar yang dihasilkannya. */
  sheet: ReturnType<typeof resolveScale>;
  scaleMode: ScaleMode;
  setScaleMode: (mode: ScaleMode) => void;
  /** Diserahkan kepada `DepthGutter` sebagai `onStretch`. */
  stretch: (dy: number, phase: "start" | "move" | "end", clientY: number) => void;
  /**
   * Dipasang pada pembungkus lembar — bukan pada panel yang bergulir, sebab
   * yang diukur adalah piksel isi lembar dan bukan piksel yang terlihat.
   */
  boxHandlers: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  };
  box: DepthBox | null;
  /**
   * Apakah klik yang baru saja tiba adalah ekor sebuah seretan — sekali baca.
   *
   * Sebuah seretan berakhir dengan `click` di tempat jari berhenti, dan layar
   * yang mengunci pickernya di situ mengunci kedalaman yang tidak pernah
   * dipilih siapa pun. Dibaca sekali lalu hangus, sebab klik berikutnya adalah
   * klik yang sesungguhnya.
   */
  consumePick: () => boolean;
  /**
   * Meregang atau memampatkan tepat satu petak kisi. Diam bila lembar ini
   * tidak berkisi — `gridPx` kosong.
   */
  stepGrid: (direction: 1 | -1) => void;
}

/**
 * Lembar kedalaman yang bisa digerakkan dengan tangan.
 *
 * `metres` adalah rentang jendela yang tergambar dan `range` kedua ujungnya;
 * keduanya boleh kosong selama datanya belum ada, dan lembarnya lalu diam.
 *
 * `headerH` adalah tinggi kepala yang menempel *di dalam* panel yang bergulir.
 * Lembar log menaruh kepalanya di luar panel dan memakai nol; lembar korelasi
 * menaruhnya di dalam, dan setiap hitungan di sini harus dikurangi setinggi
 * itu — kedalaman di puncak panel bukan kedalaman yang tergambar di sana.
 *
 * `skipBoxOn` adalah pemilih CSS: seretan yang lahir di dalam elemen yang
 * cocok bukan pilihan interval. Kepala track adalah tempat orang menggeser
 * penggeser skala, dan menggeser sejauh tujuh piksel di sana bukan permintaan
 * memperbesar interval kedalaman setinggi kepala itu.
 *
 * `panX` menyalakan sumbu mendatar: lembar log boleh lebih lebar daripada
 * panelnya, dan di sana satu tekanan bisa berakhir sebagai dua gerakan yang
 * berbeda — seret tegak memilih interval, seret mendatar menggeser lembar.
 * Lembar korelasi tidak punya sumbu itu; kolomnya membagi lebar yang ada.
 */
export function useDepthSheet({
  storageKey,
  metres,
  range,
  headerH = 0,
  skipBoxOn,
  panX = false,
  gridPx,
}: {
  storageKey: string;
  metres: number;
  range: [number, number] | null;
  headerH?: number;
  skipBoxOn?: string;
  panX?: boolean;
  /**
   * Jarak kisi mendatar lembar ini dalam piksel. Diberikan, tinggi lembar
   * dibulatkan ke kelipatannya dan `stepGrid` melangkah satu petak sekali
   * tekan; kosong, lembarnya bebas setinggi apa pun skalanya menuntut.
   */
  gridPx?: number;
}): DepthSheet {
  const [scaleMode, setScaleMode] = useStored<ScaleMode>(storageKey, "fit");
  const [paneBox, paneSize] = useElementSize<HTMLDivElement>();
  const paneRef = useRef<HTMLDivElement | null>(null);
  const lastHeight = useRef(0);
  // Kedalaman yang harus tetap di bawah kursor sesudah lembar berubah tinggi,
  // sebagai pecahan tinggi lembar yang terlihat. Kosong berarti yang
  // dipertahankan adalah bagian tengahnya — perilaku tombol + dan −.
  const zoomAnchor = useRef<number | null>(null);
  const scaleRef = useRef(200);
  // Tinggi kepala yang menempel, dibaca dari dalam pendengar roda. Ia diukur
  // pada setiap render sementara pendengarnya dipasang sekali, jadi ia tidak
  // boleh ditangkap sebagai nilai.
  const headerRef = useRef(headerH);
  // Sama seperti `headerH`: dibaca dari dalam pendengar roda yang dipasang
  // sekali, jadi ia tidak boleh ditangkap sebagai nilai.
  const panXRef = useRef(panX);
  const wheelDetach = useRef<(() => void) | null>(null);
  const stretchFrom = useRef<{ scale: number; anchor: number } | null>(null);
  // Kedalaman yang harus berada di tepi atas lembar sesudah tingginya berubah.
  // Dipakai zoom kotak, yang memindahkan pandangan alih-alih menahan satu titik.
  const pendingTop = useRef<number | null>(null);
  /**
   * Titik tekan seret pada lembar, dan gerakan yang sudah dipilihnya.
   *
   * Sumbu yang lebih dulu melewati ambang menentukan gerakan seluruh seretan
   * ini, dan sesudah itu artinya tidak berubah lagi di tengah jalan — sebuah
   * seretan yang berganti arti pada piksel keempat puluh membatalkan pekerjaan
   * yang sedang dilakukan orang dengan tangan yang sama. `x` diukur di
   * koordinat layar dan bukan koordinat lembar: lembarnya sendiri bergerak di
   * bawah jari selama digeser, dan jarak yang diukur terhadap sesuatu yang ikut
   * bergerak memberi umpan balik ke dirinya sendiri.
   */
  const dragFrom = useRef<{ y: number; x: number; scrollLeft: number } | null>(null);
  const boxing = useRef(false);
  const panning = useRef(false);
  const pickBlocked = useRef(false);
  const [scrollX, setScrollX] = useState(0);
  const [box, setBox] = useState<DepthBox | null>(null);

  /**
   * Roda memperbesar tegak di sekitar kursor.
   *
   * Pendengarnya dipasang sendiri, bukan lewat `onWheel`, karena React
   * memasang roda sebagai pendengar pasif di akar dokumen: `preventDefault`
   * di sana tidak berlaku, dan halaman ikut memperbesar dirinya bersama
   * lembarnya. Ia juga dipasang dari ref callback dan bukan dari efek, sebab
   * panelnya baru lahir sesudah kurvanya selesai dimuat.
   */
  const attachPane = useCallback(
    (el: HTMLDivElement | null) => {
      paneBox(el);
      paneRef.current = el;
      wheelDetach.current?.();
      wheelDetach.current = null;
      if (!el) return;
      const onWheel = (event: WheelEvent) => {
        if (panXRef.current && event.shiftKey) {
          // Shift + roda menggeser mendatar, sebagaimana di mana-mana. Selama
          // lembar ini boleh lebih lebar daripada panelnya, gerakan itu punya
          // sumbu untuk dijalani; meminjamnya untuk menggulir tegak berarti
          // satu-satunya cara menggeser mendatar tanpa melepas tetikus hilang.
          event.preventDefault();
          el.scrollLeft += wheelPixels(event.deltaY, event.deltaMode);
          return;
        }
        // Sapuan mendatar papan sentuh diserahkan kepada peramban apa adanya:
        // dua jari yang bergerak mendatar tidak bermaksud mengubah skala.
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        event.preventDefault();
        const rect = el.getBoundingClientRect();
        const view = rect.height - headerRef.current;
        zoomAnchor.current =
          view > 0
            ? Math.min(1, Math.max(0, (event.clientY - rect.top - headerRef.current) / view))
            : 0.5;
        // Menerus, bukan melangkah: papan sentuh melaporkan puluhan kejadian
        // kecil untuk satu gerakan jari, dan satu langkah penuh per kejadian
        // akan melempar lembar dari 1:200 ke 1:20 dalam satu sapuan.
        setScaleMode(
          dragScale(scaleRef.current, -wheelPixels(event.deltaY, event.deltaMode) * WHEEL_ZOOM_GAIN),
        );
      };
      el.addEventListener("wheel", onWheel, { passive: false });
      // Geseran mendatar dilaporkan keluar: kepala track menempel di atas
      // panel yang bergulir, bukan di dalamnya, jadi ia tidak ikut bergeser
      // sendiri dan harus digeser dengan angka.
      const onScroll = () => setScrollX(el.scrollLeft);
      el.addEventListener("scroll", onScroll, { passive: true });
      wheelDetach.current = () => {
        el.removeEventListener("wheel", onWheel);
        el.removeEventListener("scroll", onScroll);
      };
    },
    [paneBox, setScaleMode],
  );
  useEffect(() => () => wheelDetach.current?.(), []);

  const sheet = resolveScale(scaleMode, metres, Math.max(paneSize.height - headerH, 1), gridPx);
  scaleRef.current = sheet.scale;
  headerRef.current = headerH;
  panXRef.current = panX;

  /**
   * Meregang tidak boleh memindahkan kedalaman yang sedang dibaca.
   *
   * Roda menahan kedalaman di bawah kursor; tombol + dan − menahan bagian
   * tengah lembar; zoom kotak tidak menahan apa pun melainkan memindahkan
   * pandangan ke interval yang baru saja dipilih. Ketiganya adalah pertanyaan
   * yang sama — titik mana yang tidak boleh bergerak — jadi ketiganya dijawab
   * di sini.
   *
   * Berjalan pada setiap render, bukan hanya saat tinggi lembar berubah: zoom
   * kotak yang kebetulan memilih skala yang sedang berlaku tidak mengubah
   * tinggi apa pun, dan pandangannya tetap harus pindah ke interval itu.
   */
  useLayoutEffect(() => {
    const el = paneRef.current;
    const height = sheet.heightPx;
    const previous = lastHeight.current;
    lastHeight.current = height;
    if (!el) return;

    const goto = pendingTop.current;
    if (goto !== null && range) {
      pendingTop.current = null;
      zoomAnchor.current = null;
      const span = range[1] - range[0];
      el.scrollTop = span !== 0 ? ((goto - range[0]) / span) * height : 0;
      return;
    }

    if (!previous || previous === height) return;
    const anchor = zoomAnchor.current ?? 0.5;
    zoomAnchor.current = null;
    const view = Math.max(el.clientHeight - headerH, 1);
    // Piksel jangkar diukur dari puncak panel, tetapi kedalamannya dibaca di
    // ruang lembar — dan keduanya berselisih setinggi kepala yang menempel.
    const anchorPx = headerH + view * anchor;
    const held = el.scrollTop + anchorPx - headerH;
    el.scrollTop = (held * height) / previous + headerH - anchorPx;
  });

  /**
   * Meregang dengan menyeret mistar kedalaman.
   *
   * Skalanya dihitung dari titik tekan, bukan dari skala sebelumnya pada
   * setiap kejadian: menumpuk faktor per kejadian pointer membuat kecepatan
   * regang bergantung pada berapa kali tetikus melapor, yang berbeda antara
   * satu perangkat dan yang lain dan berubah sendiri ketika layar sibuk.
   */
  const stretch = (dy: number, phase: "start" | "move" | "end", clientY: number) => {
    if (phase === "start") {
      const el = paneRef.current;
      const view = el ? el.clientHeight - headerH : 0;
      // Kedalaman di bawah jari yang ditahan, bukan tengah panel: mistar
      // adalah tempat orang menunjuk lapisan yang sedang dibacanya.
      const anchor =
        el && view > 0
          ? Math.min(1, Math.max(0, (clientY - el.getBoundingClientRect().top - headerH) / view))
          : 0.5;
      stretchFrom.current = { scale: scaleRef.current, anchor };
      return;
    }
    const from = stretchFrom.current;
    if (!from) return;
    if (phase === "end") {
      stretchFrom.current = null;
      return;
    }
    zoomAnchor.current = from.anchor;
    setScaleMode(dragScale(from.scale, dy));
  };

  const commitBox = (fromY: number, toY: number, height: number) => {
    if (!range || height <= 0) return;
    const span = range[1] - range[0];
    if (!span) return;
    // Yang dijepit adalah piksel, lalu diterjemahkan — bukan sebaliknya. Pada
    // sumbu yang menurun, nilai di tepi atas kotak adalah angka yang lebih
    // besar, dan `Math.min` atas kedua kedalaman akan menempatkan pandangan di
    // tepi yang berlawanan dengan yang diseret.
    const atTop = range[0] + (Math.min(fromY, toY) / height) * span;
    const atBottom = range[0] + (Math.max(fromY, toY) / height) * span;
    const metresPicked = Math.abs(atBottom - atTop);
    if (!(metresPicked > 0)) return;
    pendingTop.current = atTop;
    setScaleMode(intervalScale(metresPicked, Math.max(paneSize.height - headerH, 1)));
  };

  /** Seretan selesai: apa pun artinya, jejaknya di elemen itu dibersihkan. */
  const endDrag = (element: HTMLElement) => {
    dragFrom.current = null;
    boxing.current = false;
    panning.current = false;
    element.style.cursor = "";
    element.style.userSelect = "";
  };

  const boxHandlers = {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      pickBlocked.current = false;
      if (event.button !== 0 || !range) return;
      if (skipBoxOn && (event.target as HTMLElement).closest?.(skipBoxOn)) return;
      const rect = event.currentTarget.getBoundingClientRect();
      dragFrom.current = {
        y: event.clientY - rect.top - headerH,
        x: event.clientX,
        scrollLeft: paneRef.current?.scrollLeft ?? 0,
      };
      boxing.current = false;
      panning.current = false;
      // Dimatikan sejak tekan, bukan sesudah ambangnya lewat: enam piksel
      // pertama sudah cukup untuk menyorot teks di sekitarnya, dan sorotan itu
      // bertahan sesudah jari dilepas — terbaca sebagai kerusakan, bukan
      // sebagai seretan. Menyeret di atas lembar berarti memilih interval
      // kedalaman, tidak pernah memilih teks.
      event.currentTarget.style.userSelect = "none";
    },
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      const from = dragFrom.current;
      if (!from) return;
      const dx = event.clientX - from.x;
      if (panning.current) {
        if (paneRef.current) paneRef.current.scrollLeft = from.scrollLeft - dx;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const y = event.clientY - rect.top - headerH;
      if (!boxing.current) {
        const dy = y - from.y;
        if (panX && Math.abs(dx) >= BOX_MIN && Math.abs(dx) > Math.abs(dy)) {
          panning.current = true;
          // Klik yang menyusul geseran bukan permintaan mengunci picker di
          // tempat jari berhenti.
          pickBlocked.current = true;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          event.currentTarget.style.cursor = "grabbing";
          if (paneRef.current) paneRef.current.scrollLeft = from.scrollLeft - dx;
          return;
        }
        if (Math.abs(dy) < BOX_MIN) return;
        boxing.current = true;
        // Ditangkap hanya sesudah ambangnya terlewati: menangkap pointer pada
        // setiap tekan memindahkan sasaran klik dari track ke pembungkus ini,
        // dan apa pun yang bisa diklik di dalamnya berhenti bekerja.
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
      setBox({ from: Math.min(from.y, y), to: Math.max(from.y, y) });
    },
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => {
      const from = dragFrom.current;
      const wasBoxing = boxing.current;
      const rect = event.currentTarget.getBoundingClientRect();
      endDrag(event.currentTarget);
      if (!wasBoxing || !from) return;
      pickBlocked.current = true;
      setBox(null);
      commitBox(from.y, event.clientY - rect.top - headerH, rect.height - headerH);
    },
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => {
      endDrag(event.currentTarget);
      setBox(null);
    },
  };

  const consumePick = () => {
    const blocked = pickBlocked.current;
    pickBlocked.current = false;
    return blocked;
  };

  /**
   * Satu petak kisi lebih tinggi (`1`) atau lebih pendek (`-1`).
   *
   * Yang ditahan adalah bagian tengah lembar, sebagaimana tombol + dan −
   * di layar lain: tekanan tombol tidak menunjuk kedalaman mana pun, dan
   * kedalaman yang sedang dipandang orang ada di tengah layarnya.
   */
  const stepGrid = (direction: 1 | -1) => {
    if (!gridPx) return;
    zoomAnchor.current = null;
    setScaleMode(stepScaleByGrid(metres, sheet.heightPx, gridPx, direction));
  };

  return {
    attachPane,
    paneHeight: paneSize.height,
    scrollX,
    sheet,
    scaleMode,
    setScaleMode,
    stretch,
    boxHandlers,
    box,
    consumePick,
    stepGrid,
  };
}
